import { createFileRoute } from "@tanstack/react-router";
import { useSuspenseQuery, queryOptions } from "@tanstack/react-query";
import { EmbedFrame } from "@/components/embed/EmbedFrame";
import { listVolatilitySnapshots } from "@/lib/backlink.functions";
import { formatShortDate } from "@/lib/site";

const volQuery = queryOptions({
  queryKey: ["embed", "volatility"],
  queryFn: () => listVolatilitySnapshots(),
  staleTime: 5 * 60 * 1000,
});

export const Route = createFileRoute("/embed/volatility")({
  head: () => ({
    meta: [
      { title: "Local SERP Volatility — Map Observer embed" },
      { name: "robots", content: "noindex,follow" },
    ],
  }),
  loader: ({ context }) => context.queryClient.ensureQueryData(volQuery),
  component: VolatilityEmbed,
});

function levelColor(level: string) {
  switch (level) {
    case "stormy":
      return { bg: "#7c1d1d", fg: "#fff5ee" };
    case "rough":
      return { bg: "#a04a1f", fg: "#fff5ee" };
    case "unsettled":
      return { bg: "#c98b2a", fg: "#1a1612" };
    default:
      return { bg: "#2f6b3a", fg: "#f3fff3" };
  }
}

function VolatilityEmbed() {
  const { data } = useSuspenseQuery(volQuery);
  const latest = data[0];
  const previous = data[1];
  const series = data.slice(0, 12).reverse();

  if (!latest) {
    return (
      <EmbedFrame name="volatility">
        <div style={{ padding: 16, fontSize: 13, color: "#3a342a" }}>
          Volatility data warming up.
        </div>
      </EmbedFrame>
    );
  }

  const colors = levelColor(latest.level);
  const delta = previous ? latest.score - previous.score : 0;
  const max = Math.max(1, ...series.map((s) => s.score));

  return (
    <EmbedFrame name="volatility">
      <div
        style={{
          fontFamily: "ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
          color: "#1a1612",
          border: "1px solid #d9cfbf",
          borderRadius: 6,
          background: "#fbf7ef",
          padding: "14px 16px",
          display: "flex",
          alignItems: "center",
          gap: 14,
        }}
      >
        <div
          style={{
            background: colors.bg,
            color: colors.fg,
            borderRadius: 4,
            padding: "10px 14px",
            minWidth: 78,
            textAlign: "center",
          }}
        >
          <div style={{ fontSize: 24, fontWeight: 700, lineHeight: 1 }}>{latest.score.toFixed(1)}</div>
          <div style={{ fontSize: 10, letterSpacing: 1, textTransform: "uppercase", marginTop: 4 }}>/ 10</div>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 11, letterSpacing: 1, textTransform: "uppercase", color: "#6b6357" }}>
            Local SERP Weather
          </div>
          <div style={{ fontSize: 16, fontWeight: 600, textTransform: "capitalize" }}>{latest.level}</div>
          <div style={{ fontSize: 11, color: "#6b6357", marginTop: 2 }}>
            {formatShortDate(latest.snapshot_date)}
            {previous ? ` · ${delta >= 0 ? "+" : ""}${delta.toFixed(1)} wk-over-wk` : ""}
          </div>
        </div>
        <svg width="96" height="36" viewBox="0 0 96 36" style={{ flexShrink: 0 }} aria-hidden>
          {series.map((s, i) => {
            const x = (i / Math.max(1, series.length - 1)) * 92 + 2;
            const y = 32 - (s.score / max) * 28;
            return <circle key={s.id} cx={x} cy={y} r={1.6} fill={colors.bg} />;
          })}
          <polyline
            fill="none"
            stroke={colors.bg}
            strokeWidth="1.4"
            points={series
              .map((s, i) => {
                const x = (i / Math.max(1, series.length - 1)) * 92 + 2;
                const y = 32 - (s.score / max) * 28;
                return `${x},${y}`;
              })
              .join(" ")}
          />
        </svg>
      </div>
    </EmbedFrame>
  );
}
